import { useState, useEffect } from "react";import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Edit, Trash2, MessageSquare, Ban, RotateCcw, Loader2, Save, Eye, History } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function AdminMessages() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [tab, setTab] = useState("reasons");
  const [reasons, setReasons] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editReason, setEditReason] = useState(null);
  const [showCreateReason, setShowCreateReason] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        axios.get(`${API}/admin/cancellation-reasons`),
        axios.get(`${API}/admin/notification-templates`),
      ]);
      setReasons(r.data);
      setTemplates(t.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReason = async (payload, reasonId) => {
    try {
      if (reasonId) {
        const res = await axios.put(`${API}/admin/cancellation-reasons/${reasonId}`, payload);
        setReasons((prev) => prev.map((r) => (r.reason_id === reasonId ? res.data : r)));
      } else {
        const res = await axios.post(`${API}/admin/cancellation-reasons`, payload);
        setReasons((prev) => [...prev, res.data]);
      }
      toast.success(t("admin_messages:saved_toast"));
      setEditReason(null);
      setShowCreateReason(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("admin_messages:failed_toast"));
    }
  };

  const handleDeleteReason = async (r) => {
    if (!window.confirm(`Delete "${r.label_en}"?`)) return;
    try {
      await axios.delete(`${API}/admin/cancellation-reasons/${r.reason_id}`);
      setReasons((prev) => prev.filter((x) => x.reason_id !== r.reason_id));
      toast.success(t("admin_messages:saved_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("admin_messages:failed_toast"));
    }
  };

  const handleToggleReasonActive = async (r) => {
    const next = !r.is_active;
    setReasons((prev) => prev.map((x) => (x.reason_id === r.reason_id ? { ...x, is_active: next } : x)));
    try {
      await axios.put(`${API}/admin/cancellation-reasons/${r.reason_id}`, { is_active: next });
    } catch (err) {
      toast.error(t("admin_messages:failed_toast"));
      fetchAll();
    }
  };

  const handleSaveTemplate = async (key, payload) => {
    try {
      const res = await axios.put(`${API}/admin/notification-templates/${key}`, payload);
      setTemplates((prev) => prev.map((x) => (x.key === key ? res.data : x)));
      toast.success(t("admin_messages:saved_toast"));
      setEditTemplate(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("admin_messages:failed_toast"));
    }
  };

  const handleResetTemplate = async (key) => {
    if (!window.confirm("Reset this template to the built-in default?")) return;
    try {
      await axios.delete(`${API}/admin/notification-templates/${key}`);
      await fetchAll();
      toast.success(t("admin_messages:reset_default_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("admin_messages:failed_toast"));
    }
  };

  return (
    <div data-testid="admin-messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><MessageSquare className="h-6 w-6 text-primary" />{t("admin_messages:page_title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("admin_messages:page_subtitle")}
          </p>
        </div>
        {tab === "reasons" && (
          <Dialog open={showCreateReason} onOpenChange={setShowCreateReason}>
            <DialogTrigger asChild>
              <Button data-testid="new-reason-btn">
                <Plus className="h-4 w-4 mr-2" /> {t("admin_messages:new_reason")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("admin_messages:new_reason_dialog_title")}</DialogTitle>
              </DialogHeader>
              <ReasonForm onSubmit={(p) => handleSaveReason(p)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <Button
          variant={tab === "reasons" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("reasons")}
          data-testid="tab-reasons"
        >
          <Ban className="h-4 w-4 mr-2" /> {t("admin_messages:tab_reasons")} ({reasons.length})
        </Button>
        <Button
          variant={tab === "templates" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("templates")}
          data-testid="tab-templates"
        >
          <MessageSquare className="h-4 w-4 mr-2" /> {t("admin_messages:tab_templates")} ({templates.length})
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse bg-muted h-64 rounded-xl" />
      ) : tab === "reasons" ? (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_1fr_80px_80px_110px] items-center px-4 py-3 border-b border-border text-sm font-medium text-muted-foreground bg-muted/30">
            <div>{t("admin_messages:col_label_en")}</div>
            <div>{t("admin_messages:col_label_el")}</div>
            <div>{t("admin_messages:col_order")}</div>
            <div>{t("admin_messages:col_active")}</div>
            <div className="text-right">{t("admin_messages:col_actions")}</div>
          </div>
          {reasons.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              {t("admin_messages:no_reasons")}
            </div>
          ) : (
            reasons.map((r) => (
              <div
                key={r.reason_id}
                className="grid grid-cols-[1fr_1fr_80px_80px_110px] items-center px-4 py-3 border-b border-border text-sm"
                data-testid={`reason-row-${r.reason_id}`}
              >
                <div className="font-medium truncate">{r.label_en}</div>
                <div className="text-muted-foreground truncate">{r.label_el || "\u2014"}</div>
                <div className="text-muted-foreground">{r.sort_order}</div>
                <div>
                  <Switch
                    checked={r.is_active}
                    onCheckedChange={() => handleToggleReasonActive(r)}
                    data-testid={`reason-toggle-${r.reason_id}`}
                  />
                </div>
                <div className="text-right flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditReason(r)}
                    data-testid={`reason-edit-${r.reason_id}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteReason(r)}
                    data-testid={`reason-delete-${r.reason_id}`}
                  >
                    <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tpl) => (
            <div
              key={tpl.key}
              className="bg-card rounded-xl border border-border p-5"
              data-testid={`template-row-${tpl.key}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">
                    {(lang === "el" && tpl.title_el) || tpl.title_en}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(lang === "el" && tpl.description_el) || tpl.description}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditTemplate(tpl)}
                    data-testid={`template-edit-${tpl.key}`}
                  >
                    <Edit className="h-3.5 w-3.5 mr-1" /> {t("admin_messages:edit")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetTemplate(tpl.key)}
                    title={t("admin_messages:reset_to_default")}
                    data-testid={`template-reset-${tpl.key}`}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-muted/40 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">🇬🇧 EN</p>
                  <p className="font-medium text-foreground">{tpl.title_en}</p>
                  <p className="text-muted-foreground text-xs mt-1">{tpl.message_en}</p>
                </div>
                <div className="p-3 bg-muted/40 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">🇬🇷 EL</p>
                  <p className="font-medium text-foreground">{tpl.title_el}</p>
                  <p className="text-muted-foreground text-xs mt-1">{tpl.message_el}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editReason && (
        <Dialog open={!!editReason} onOpenChange={() => setEditReason(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin_messages:edit_reason")}</DialogTitle>
            </DialogHeader>
            <ReasonForm reason={editReason} onSubmit={(p) => handleSaveReason(p, editReason.reason_id)} />
          </DialogContent>
        </Dialog>
      )}

      {editTemplate && (
        <TemplateEditDialog
          template={editTemplate}
          onClose={() => setEditTemplate(null)}
          onSave={(p) => handleSaveTemplate(editTemplate.key, p)}
        />
      )}
    </div>
  );
}

function ReasonForm({ reason, onSubmit }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    label_en: reason?.label_en || "",
    label_el: reason?.label_el || "",
    sort_order: reason?.sort_order || 0,
    is_active: reason?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>{t("admin_messages:label_en_required")}</Label>
        <Textarea
          value={form.label_en}
          onChange={(e) => setForm({ ...form, label_en: e.target.value })}
          required
          rows={2}
          placeholder={t("admin_messages:label_en_placeholder")}
          className="mt-1"
          data-testid="reason-label-en"
        />
      </div>
      <div>
        <Label>{t("admin_messages:label_el_optional")}</Label>
        <Textarea
          value={form.label_el}
          onChange={(e) => setForm({ ...form, label_el: e.target.value })}
          rows={2}
          placeholder={t("admin_messages:label_el_placeholder")}
          className="mt-1"
          data-testid="reason-label-el"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>{t("admin_messages:sort_order")}</Label>
          <Input
            type="number"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
        <div className="flex items-end">
          <div className="flex items-center gap-3 mt-1">
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
            />
            <span className="text-sm">{t("admin_messages:active")}</span>
          </div>
        </div>
      </div>
      <Button type="submit" disabled={saving} className="w-full" data-testid="reason-submit-btn">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        {t("admin_messages:save")}
      </Button>
    </form>
  );
}

function TemplateEditDialog({ template, onClose, onSave }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [form, setForm] = useState({
    title_en: template.title_en || "",
    title_el: template.title_el || "",
    message_en: template.message_en || "",
    message_el: template.message_el || "",
  });
  const [saving, setSaving] = useState(false);
  const [langTab, setLangTab] = useState("en");
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  // Debounced live preview — re-render whenever the form changes
  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/admin/notification-templates/preview`,
          form,
          { signal: ctrl.signal },
        );
        setPreview(res.data);
      } catch (e) {
        // silent — preview is best-effort
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [form]);

  // Load audit log lazily when admin opens it
  useEffect(() => {
    if (!showAudit || audit.length > 0) return;
    axios
      .get(`${API}/admin/notification-templates-audit?key=${template.key}`)
      .then((res) => setAudit(res.data))
      .catch(() => {});
  }, [showAudit, audit.length, template.key]);

  // Extract unique placeholders like {customer_name} so admins see what they can use
  const placeholders = Array.from(
    new Set([
      ...(template.message_en?.match(/\{[a-z_]+\}/g) || []),
      ...(template.message_el?.match(/\{[a-z_]+\}/g) || []),
    ]),
  );

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="template-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3">
            <span>{t("admin_messages:edit_template")} · {template.key}</span>
            <button
              type="button"
              onClick={() => setShowAudit(!showAudit)}
              className="text-xs font-normal text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              data-testid="template-audit-toggle"
            >
              <History className="h-3.5 w-3.5" />
              {showAudit ? t("admin_messages:hide_history") : t("admin_messages:edit_history")}
            </button>
          </DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {(lang === "el" && template.description_el) || template.description}
        </p>
        {placeholders.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-xs text-muted-foreground mt-0.5">{t("admin_messages:placeholders")}</span>
            {placeholders.map((p) => (
              <code key={p} className="text-[11px] bg-muted px-1.5 py-0.5 rounded font-mono">
                {p}
              </code>
            ))}
          </div>
        )}

        {showAudit && (
          <div className="border border-border rounded-lg p-3 bg-muted/30 max-h-48 overflow-y-auto" data-testid="template-audit-log">
            {audit.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">
                {t("admin_messages:no_edits")}
              </p>
            ) : (
              <div className="space-y-2">
                {audit.map((a) => (
                  <div key={a.audit_id} className="text-xs border-b border-border/50 pb-2 last:border-0">
                    <div className="flex items-center justify-between text-muted-foreground">
                      <span className="font-medium text-foreground">{a.actor_email || "—"}</span>
                      <span>{a.created_at?.replace("T", " ").slice(0, 16)}</span>
                    </div>
                    <div className="mt-1 text-muted-foreground">
                      {t("admin_messages:changed")} <span className="font-medium">{(a.changed_fields || []).join(", ")}</span>
                    </div>
                    {Object.entries(a.before || {}).map(([f, v]) => (
                      <div key={f} className="mt-1 grid grid-cols-2 gap-2">
                        <div className="text-red-600 line-through truncate" title={v}>
                          {v}
                        </div>
                        <div className="text-emerald-700 truncate" title={(a.after || {})[f]}>
                          {(a.after || {})[f]}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
          <button
            type="button"
            onClick={() => setLangTab("en")}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              langTab === "en" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="template-lang-en"
          >
            {t("admin_messages:english_label")}
          </button>
          <button
            type="button"
            onClick={() => setLangTab("el")}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              langTab === "el" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="template-lang-el"
          >
            {t("admin_messages:greek_label")}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            {langTab === "en" ? (
              <div className="space-y-3">
                <div>
                  <Label>{t("admin_messages:title_en")}</Label>
                  <Input
                    value={form.title_en}
                    onChange={(e) => setForm({ ...form, title_en: e.target.value })}
                    className="mt-1"
                    data-testid="template-title-en"
                  />
                </div>
                <div>
                  <Label>{t("admin_messages:message_en")}</Label>
                  <Textarea
                    value={form.message_en}
                    onChange={(e) => setForm({ ...form, message_en: e.target.value })}
                    rows={4}
                    className="mt-1 font-mono text-sm"
                    data-testid="template-message-en"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>{t("admin_messages:title_el")}</Label>
                  <Input
                    value={form.title_el}
                    onChange={(e) => setForm({ ...form, title_el: e.target.value })}
                    className="mt-1"
                    placeholder={t("admin_messages:title_el_placeholder")}
                    data-testid="template-title-el"
                  />
                </div>
                <div>
                  <Label>{t("admin_messages:message_el")}</Label>
                  <Textarea
                    value={form.message_el}
                    onChange={(e) => setForm({ ...form, message_el: e.target.value })}
                    rows={4}
                    className="mt-1 font-mono text-sm"
                    placeholder={t("admin_messages:message_el_placeholder")}
                    data-testid="template-message-el"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Live preview panel */}
          <div className="space-y-2" data-testid="template-preview">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Eye className="h-3.5 w-3.5" />
              {t("admin_messages:live_preview")}
            </div>
            <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                {langTab === "en" ? t("admin_messages:english_notification") : t("admin_messages:greek_notification")}
              </p>
              <p className="font-medium text-foreground" data-testid="preview-title">
                {langTab === "en"
                  ? preview?.title_en || form.title_en
                  : preview?.title_el || form.title_el}
              </p>
              <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap" data-testid="preview-message">
                {langTab === "en"
                  ? preview?.message_en || form.message_en
                  : preview?.message_el || form.message_el}
              </p>
            </div>
            {preview?.sample && (
              <div className="text-[10px] text-muted-foreground space-y-0.5 pt-1">
                <p className="font-medium uppercase tracking-wide">{t("admin_messages:sample_data_used")}</p>
                {Object.entries(preview.sample).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <code className="font-mono">{`{${k}}`}</code>
                    <span className="truncate">{String(v)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("admin_messages:cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="template-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t("admin_messages:save_template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
